import { useState } from 'react';
import { DEFAULT_JOBS_COLOR_PALETTE, ENGAGEMENT_TYPE_COLOR_MAP, JOBS_COLOR_PALETTE_KEY, JOBS_COLOR_PALETTE_PRESETS_KEY, JOBS_EVENT_COLORS, JOBS_STATUS_LABELS, JobsColorPalette } from 'dashboard409a/pages/Workboard/common/constants';
import { useLocalStorage } from 'services/hooks/useLocalStorage';
import {
  getPreference,
  migrateLegacyLocalStorageKey,
  setPreference,
} from 'services/storage/preferencesIndexedDB';
import { Logger } from 'utilities/logger';

export type ColorPalettePreset = {
  name: string;
  palette: JobsColorPalette;
};

let cachedJobsColorPalette: JobsColorPalette = DEFAULT_JOBS_COLOR_PALETTE;

const refreshJobsColorPaletteCache = async (): Promise<JobsColorPalette> => {
  await migrateLegacyLocalStorageKey(JOBS_COLOR_PALETTE_KEY);
  const saved = await getPreference<Partial<JobsColorPalette>>(JOBS_COLOR_PALETTE_KEY);
  cachedJobsColorPalette = {
    ...DEFAULT_JOBS_COLOR_PALETTE,
    ...(saved ?? {}),
  };
  return cachedJobsColorPalette;
};

// Keep the promise so save/reset can await it before merging with the loaded value.
const jobsCacheReady: Promise<void> = refreshJobsColorPaletteCache().then(() => undefined);

export const getJobsColorPalette = (): JobsColorPalette => cachedJobsColorPalette;

export const getEngagementTypeColor = (engagementType: string): string => ENGAGEMENT_TYPE_COLOR_MAP[engagementType as keyof typeof ENGAGEMENT_TYPE_COLOR_MAP] || '#000000';

export const saveJobsColorPalette = (palette: Partial<JobsColorPalette>): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // Wait for the initial load so the merge uses the user's stored palette, not the default.
  jobsCacheReady.then(() => {
    const merged = { ...cachedJobsColorPalette, ...palette };
    cachedJobsColorPalette = merged;
    setPreference(JOBS_COLOR_PALETTE_KEY, merged).catch(() => {
      Logger.error('Error saving jobs color palette', new Error('Error saving jobs color palette'));
    });
  });
};

export const resetJobsColorPalette = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  jobsCacheReady.then(() => {
    cachedJobsColorPalette = DEFAULT_JOBS_COLOR_PALETTE;
    setPreference(JOBS_COLOR_PALETTE_KEY, DEFAULT_JOBS_COLOR_PALETTE).catch(() => {
      Logger.error('Error resetting jobs color palette', new Error('Error resetting jobs color palette'));
    });
  });
};

// Get status color with localStorage fallback
export const getStatusColor = (status: number): string => {
  const palette = getJobsColorPalette();
  const paletteKeys = Object.keys(JOBS_STATUS_LABELS) as Array<keyof JobsColorPalette>;
  const paletteKey = paletteKeys[status];
  
  return paletteKey ? palette[paletteKey] : JOBS_EVENT_COLORS.DEFAULT;
};

// Helper to convert color with alpha to solid color for color input
export const toSolidColor = (color: string): string => {
  if (!color) {
    return '#000000';
  }
  // If color has 8 characters (with alpha), strip the alpha
  if (color.length === 9 && color.startsWith('#')) {
    return color.slice(0, 7);
  }
  // If color is rgba format, convert to hex
  if (color.startsWith('rgba')) {
    const matches = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (matches) {
      const r = parseInt(matches[1], 10).toString(16).padStart(2, '0');
      const g = parseInt(matches[2], 10).toString(16).padStart(2, '0');
      const b = parseInt(matches[3], 10).toString(16).padStart(2, '0');
      return `#${r}${g}${b}`;
    }
  }
  return color;
};


/**
 * Custom hook for managing color palette state and operations
 * @returns Object containing colorPalette, presets, and handler functions
 */
export const useJobsColorPalette = () => {
  const [colorPalette, setColorPalette] = useLocalStorage<JobsColorPalette>(
    JOBS_COLOR_PALETTE_KEY,
    DEFAULT_JOBS_COLOR_PALETTE
  );
  const [presets, setPresets] = useLocalStorage<ColorPalettePreset[]>(
    JOBS_COLOR_PALETTE_PRESETS_KEY,
    []
  );
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [presetName, setPresetName] = useState('');

  const handleColorChange = (key: keyof JobsColorPalette, color: string) => {
    setColorPalette(prev => {
      const next = { ...prev, [key]: color };
      cachedJobsColorPalette = next;
      return next;
    });
  };

  const handleReset = () => {
    cachedJobsColorPalette = DEFAULT_JOBS_COLOR_PALETTE;
    setColorPalette(DEFAULT_JOBS_COLOR_PALETTE);
  };

  const handleSavePreset = () => {
    if (presetName.trim()) {
      const existingIndex = presets.findIndex(p => p.name === presetName.trim());
      const updatedPresets = existingIndex >= 0
        ? presets.map((p, idx) => idx === existingIndex ? { name: presetName.trim(), palette: colorPalette } : p)
        : [...presets, { name: presetName.trim(), palette: colorPalette }];
      setPresets(updatedPresets);
      setPresetName('');
      setSavePresetOpen(false);
    }
  };

  const handleOpenSavePreset = () => {
    setSavePresetOpen(true);
  };

  const handleCloseSavePreset = () => {
    setSavePresetOpen(false);
    setPresetName('');
  };

  const handleApplyPreset = (name: string) => {
    const preset = presets.find(p => p.name === name);
    if (preset) {
      setColorPalette(preset.palette);
      cachedJobsColorPalette = preset.palette;
    }
  };

  const handleDeletePreset = (name: string) => {
    setPresets(presets.filter(p => p.name !== name));
  };

  return {
    colorPalette,
    presets,
    savePresetOpen,
    presetName,
    setPresetName,
    handleColorChange,
    handleReset,
    handleSavePreset,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleApplyPreset,
    handleDeletePreset,
  };
};